import {
  GlassBadge,
  GlassButton,
  GlassCard,
  GlassCardContent,
  GlassCardHeader,
} from '@merritrac/shared/components';
import {
  AlertCircle,
  ArrowLeft,
  Building2,
  Calendar,
  CheckCircle2,
  ClipboardList,
  Clock,
  ExternalLink,
  Loader2,
  MapPin,
  XCircle,
} from 'lucide-react';
import { useNavigate, useParams, Link } from 'react-router';

import { useGraphQL } from '../hooks/useGraphQL.js';

import type { ReactElement } from 'react';

/* ── Types ── */

type RequestStatus = 'pending' | 'approved' | 'converted' | 'rejected';
type UrgencyLevel = 'low' | 'normal' | 'urgent';
type GlassBadgeVariant = 'info' | 'success' | 'warning' | 'danger' | 'default';

interface ServiceRequestDetailNode {
  id: string;
  description: string;
  urgency: string;
  status: string;
  createdAt: string;
  updatedAt: string;
  serviceOrderAk: number | null;
  photoR2Keys: string[] | null;
  property: {
    name: string;
    address: string | null;
    city: string | null;
    state: string | null;
  } | null;
  contact: { firstName: string; lastName: string } | null;
}

interface ServiceRequestData {
  serviceRequest: ServiceRequestDetailNode | null;
}

/* ── GraphQL ── */

const GET_SERVICE_REQUEST = `
  query GetServiceRequest($id: ID!) {
    serviceRequest(id: $id) {
      id
      description
      urgency
      status
      createdAt
      updatedAt
      serviceOrderAk
      photoR2Keys
      property {
        name
        address
        city
        state
      }
      contact { firstName lastName }
    }
  }
`;

/* ── Status helpers ── */

function normaliseStatus(raw: string): RequestStatus {
  const lower = raw.toLowerCase();
  if (lower === 'approved') {
    return 'approved';
  }
  if (lower === 'converted') {
    return 'converted';
  }
  if (lower === 'rejected') {
    return 'rejected';
  }
  return 'pending';
}

function normaliseUrgency(raw: string): UrgencyLevel {
  const lower = raw.toLowerCase();
  if (lower === 'urgent') {
    return 'urgent';
  }
  if (lower === 'low') {
    return 'low';
  }
  return 'normal';
}

const statusBadgeVariant: Record<RequestStatus, GlassBadgeVariant> = {
  pending: 'warning',
  approved: 'info',
  converted: 'success',
  rejected: 'danger',
};

const statusLabel: Record<RequestStatus, string> = {
  pending: 'Pending',
  approved: 'Approved',
  converted: 'Converted to Order',
  rejected: 'Rejected',
};

const urgencyBadgeVariant: Record<UrgencyLevel, GlassBadgeVariant> = {
  low: 'default',
  normal: 'info',
  urgent: 'danger',
};

const urgencyLabel: Record<UrgencyLevel, string> = {
  low: 'Low',
  normal: 'Normal',
  urgent: 'Urgent',
};

/* ── Helpers ── */

function formatDateTime(iso: string): string {
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

/* ── Sub-components ── */

interface DetailFieldProps {
  label: string;
  value: string;
  icon?: ReactElement;
}

function DetailField({ label, value, icon }: DetailFieldProps): ReactElement {
  return (
    <div className="flex flex-col gap-0.5">
      <p className="text-xs font-medium text-text-secondary uppercase tracking-wide">{label}</p>
      <div className="flex items-center gap-1.5 text-sm text-text">
        {icon !== undefined && <span className="text-text-tertiary shrink-0">{icon}</span>}
        <span>{value}</span>
      </div>
    </div>
  );
}

interface StatusCalloutProps {
  status: RequestStatus;
  serviceOrderAk: number | null;
}

function StatusCallout({ status, serviceOrderAk }: StatusCalloutProps): ReactElement | null {
  if (status === 'converted' && serviceOrderAk !== null) {
    return (
      <div className="flex items-start gap-3 rounded-lg border border-success/30 bg-success/5 px-4 py-3">
        <CheckCircle2 size={16} className="mt-0.5 shrink-0 text-success" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-text">Request approved and scheduled</p>
          <p className="text-xs text-text-secondary mt-0.5">
            A service order has been created for this request.
          </p>
        </div>
        <Link
          to={`/orders/${String(serviceOrderAk)}`}
          className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline shrink-0"
        >
          View Order
          <ExternalLink size={11} />
        </Link>
      </div>
    );
  }

  if (status === 'pending' || status === 'approved') {
    return (
      <div className="flex items-start gap-3 rounded-lg border border-primary/20 bg-primary/5 px-4 py-3">
        <Clock size={16} className="mt-0.5 shrink-0 text-primary" />
        <div className="min-w-0">
          <p className="text-sm font-medium text-text">Your request is being reviewed</p>
          <p className="text-xs text-text-secondary mt-0.5">
            We will follow up with you once a decision has been made.
          </p>
        </div>
      </div>
    );
  }

  if (status === 'rejected') {
    return (
      <div className="flex items-start gap-3 rounded-lg border border-danger/30 bg-danger-soft px-4 py-3">
        <XCircle size={16} className="mt-0.5 shrink-0 text-danger" />
        <div className="min-w-0">
          <p className="text-sm font-medium text-danger">This request was not approved</p>
          <p className="text-xs text-danger/80 mt-0.5">
            If you have questions, please contact your property manager.
          </p>
        </div>
      </div>
    );
  }

  return null;
}

interface NotFoundProps {
  id: string;
  onBack: () => void;
}

function NotFound({ id, onBack }: NotFoundProps): ReactElement {
  return (
    <div className="flex flex-col items-center justify-center py-24 gap-4">
      <ClipboardList size={48} className="text-text-tertiary" />
      <div className="text-center">
        <p className="text-lg font-semibold text-text">Request not found</p>
        <p className="text-sm text-text-secondary mt-1">
          No service request with ID &ldquo;{id}&rdquo; could be located.
        </p>
      </div>
      <GlassButton variant="secondary" size="md" onClick={onBack}>
        <ArrowLeft size={14} />
        Back to Requests
      </GlassButton>
    </div>
  );
}

/* ── Page ── */

export function RequestDetailPage(): ReactElement {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const resolvedId = id ?? '';

  const { data, loading, error, refetch } = useGraphQL<ServiceRequestData>(GET_SERVICE_REQUEST, {
    id: resolvedId,
  });

  function handleBack(): void {
    void navigate('/my-requests');
  }

  /* Loading */
  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-24 gap-4">
        <Loader2 size={40} className="text-text-tertiary animate-spin" />
        <p className="text-sm text-text-secondary">Loading request&hellip;</p>
      </div>
    );
  }

  /* Error */
  if (error !== null) {
    return (
      <div className="space-y-6">
        <div>
          <GlassButton variant="ghost" size="sm" onClick={handleBack} className="-ml-1">
            <ArrowLeft size={14} />
            My Requests
          </GlassButton>
        </div>
        <div className="flex items-start gap-3 rounded-lg border border-danger/30 bg-danger-soft px-4 py-3 text-sm text-danger">
          <AlertCircle size={16} className="mt-0.5 shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="font-medium">Failed to load request</p>
            <p className="text-xs mt-0.5 text-danger/80">{error}</p>
          </div>
          <GlassButton variant="ghost" size="sm" onClick={refetch} className="shrink-0 text-danger">
            Retry
          </GlassButton>
        </div>
      </div>
    );
  }

  /* Not found */
  if (data?.serviceRequest === null || data?.serviceRequest === undefined) {
    return <NotFound id={resolvedId} onBack={handleBack} />;
  }

  const node = data.serviceRequest;
  const status = normaliseStatus(node.status);
  const urgency = normaliseUrgency(node.urgency);

  const propertyAddress =
    node.property !== null
      ? [node.property.address, node.property.city, node.property.state].filter(Boolean).join(', ')
      : '';

  const hasPhotos = Array.isArray(node.photoR2Keys) && node.photoR2Keys.length > 0;

  return (
    <div className="space-y-6 max-w-2xl">
      {/* Back */}
      <div>
        <GlassButton variant="ghost" size="sm" onClick={handleBack} className="-ml-1">
          <ArrowLeft size={14} />
          My Requests
        </GlassButton>
      </div>

      {/* Status callout */}
      <StatusCallout status={status} serviceOrderAk={node.serviceOrderAk} />

      {/* Header card */}
      <GlassCard>
        <GlassCardContent className="py-5">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div className="flex flex-col gap-2 min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-mono text-xs font-semibold text-text-tertiary">
                  Request #{node.id}
                </span>
                <GlassBadge variant={statusBadgeVariant[status]}>{statusLabel[status]}</GlassBadge>
                <GlassBadge variant={urgencyBadgeVariant[urgency]}>
                  {urgencyLabel[urgency]}
                </GlassBadge>
              </div>
              <h1 className="text-lg font-semibold text-text leading-snug">{node.description}</h1>
            </div>
            <div className="flex flex-col gap-1 sm:items-end text-xs text-text-tertiary shrink-0">
              <div className="flex items-center gap-1">
                <Calendar size={11} />
                <span>Submitted {formatDateTime(node.createdAt)}</span>
              </div>
              {node.updatedAt !== node.createdAt && (
                <div className="flex items-center gap-1">
                  <Clock size={11} />
                  <span>Updated {formatDateTime(node.updatedAt)}</span>
                </div>
              )}
            </div>
          </div>
        </GlassCardContent>
      </GlassCard>

      {/* Property details */}
      {node.property !== null && (
        <GlassCard>
          <GlassCardHeader>
            <div className="flex items-center gap-2">
              <Building2 size={15} className="text-text-secondary" />
              <h2 className="text-base font-semibold text-text">Property</h2>
            </div>
          </GlassCardHeader>
          <GlassCardContent>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <DetailField
                label="Property Name"
                value={node.property.name}
                icon={<Building2 size={13} />}
              />
              {propertyAddress !== '' && (
                <DetailField label="Address" value={propertyAddress} icon={<MapPin size={13} />} />
              )}
            </div>
          </GlassCardContent>
        </GlassCard>
      )}

      {/* Request details */}
      <GlassCard>
        <GlassCardHeader>
          <div className="flex items-center gap-2">
            <ClipboardList size={15} className="text-text-secondary" />
            <h2 className="text-base font-semibold text-text">Request Details</h2>
          </div>
        </GlassCardHeader>
        <GlassCardContent>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <DetailField label="Status" value={statusLabel[status]} />
            <DetailField label="Urgency" value={urgencyLabel[urgency]} />
            <DetailField
              label="Submitted"
              value={formatDateTime(node.createdAt)}
              icon={<Calendar size={13} />}
            />
            {node.updatedAt !== node.createdAt && (
              <DetailField
                label="Last Updated"
                value={formatDateTime(node.updatedAt)}
                icon={<Clock size={13} />}
              />
            )}
            {node.contact !== null && (
              <DetailField
                label="Submitted By"
                value={`${node.contact.firstName} ${node.contact.lastName}`}
              />
            )}
            {status === 'converted' && node.serviceOrderAk !== null && (
              <div className="flex flex-col gap-0.5">
                <p className="text-xs font-medium text-text-secondary uppercase tracking-wide">
                  Service Order
                </p>
                <Link
                  to={`/orders/${String(node.serviceOrderAk)}`}
                  className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
                >
                  #{String(node.serviceOrderAk)}
                  <ExternalLink size={12} />
                </Link>
              </div>
            )}
          </div>

          {/* Photo count notice */}
          {hasPhotos && (
            <div className="mt-4 pt-4 border-t border-[var(--glass-border-subtle)]">
              <p className="text-xs text-text-tertiary">
                {String((node.photoR2Keys ?? []).length)} photo
                {(node.photoR2Keys ?? []).length !== 1 ? 's' : ''} attached to this request.
              </p>
            </div>
          )}
        </GlassCardContent>
      </GlassCard>
    </div>
  );
}
